import type {
  BodyFontId,
  DisplayFontId,
  MotifId,
  OutputSize,
  PaletteId,
  StudioState,
  TemplateId
} from "./types";

export const OUTPUT_DIMENSIONS: Record<OutputSize, { width: number; height: number }> = {
  square: { width: 1080, height: 1080 },
  wide: { width: 1920, height: 1080 }
};

export const VIDEO_DIMENSIONS: Record<OutputSize, { width: number; height: number }> = {
  square: { width: 2160, height: 2160 },
  wide: { width: 3840, height: 2160 }
};

export const MAX_PRESETS = 30;
export const MAX_PRESET_STORAGE_LENGTH = 200_000;

export const PREVIEW_DIMENSIONS: Record<OutputSize, { width: number; height: number }> = {
  square: { width: 760, height: 760 },
  wide: { width: 960, height: 540 }
};

export const DISPLAY_FONTS: Record<
  DisplayFontId,
  {
    label: string;
    family: string;
    tracking: number;
    lineHeight: number;
  }
> = {
  staatliches: {
    label: "Staatliches",
    family: '"Staatliches", sans-serif',
    tracking: -0.022,
    lineHeight: 0.92
  },
  "bebas-neue": {
    label: "Bebas Neue",
    family: '"Bebas Neue", sans-serif',
    tracking: -0.012,
    lineHeight: 0.9
  },
  "archivo-black": {
    label: "Archivo Black",
    family: '"Archivo Black", sans-serif',
    tracking: -0.032,
    lineHeight: 0.94
  }
};
export const DISPLAY_FONT_IDS: DisplayFontId[] = [
  "staatliches",
  "bebas-neue",
  "archivo-black"
];

export const BODY_FONTS: Record<
  BodyFontId,
  { label: string; family: string }
> = {
  lato: { label: "Lato", family: '"Lato", sans-serif' },
  barlow: { label: "Barlow", family: '"Barlow", sans-serif' }
};
export const BODY_FONT_IDS: BodyFontId[] = ["lato", "barlow"];

export const PALETTES: Record<
  PaletteId,
  { label: string; colors: [string, string, string, string]; ink: string; signal: string }
> = {
  noir: {
    label: "Wavelet Noir",
    colors: ["#11100f", "#2a2520", "#6f4d37", "#e57d42"],
    ink: "#f1ece2",
    signal: "#ef8648"
  },
  ember: {
    label: "Signal Ember",
    colors: ["#120f0d", "#351a12", "#a94222", "#f2a265"],
    ink: "#fff1df",
    signal: "#ff7a3d"
  },
  violet: {
    label: "Violet Drift",
    colors: ["#11101a", "#292057", "#5948d8", "#b480ff"],
    ink: "#f1ecff",
    signal: "#ef884a"
  },
  cobalt: {
    label: "Cobalt Bloom",
    colors: ["#0d111b", "#15316f", "#3158e4", "#8ea6ff"],
    ink: "#eff2ff",
    signal: "#ef884a"
  },
  bone: {
    label: "Bone Minimal",
    colors: ["#d6ccbb", "#eee7da", "#9e9180", "#332c26"],
    ink: "#1a1714",
    signal: "#dd6535"
  }
};

export const MOTIFS: Array<{ id: MotifId; label: string }> = [
  { id: "mesh", label: "Atmospheric mesh" },
  { id: "bokeh", label: "Bokeh field" },
  { id: "arches", label: "Geometric arches" },
  { id: "light-columns", label: "Light columns" },
  { id: "orbital-halo", label: "Orbital halo" }
];

export const STYLE_PRESETS: Array<{
  id: PaletteId;
  label: string;
  motif: MotifId;
  style: Omit<StudioState["style"], "palette" | "motif">;
}> = [
  {
    id: "noir",
    label: "Wavelet Noir",
    motif: "light-columns",
    style: {
      variance: 0.22,
      vibrance: 0.74,
      brightness: 0.82,
      grain: 0.17,
      bokeh: 0.2,
      vignette: 0.46
    }
  },
  {
    id: "ember",
    label: "Signal Ember",
    motif: "orbital-halo",
    style: {
      variance: 0.44,
      vibrance: 0.96,
      brightness: 0.9,
      grain: 0.13,
      bokeh: 0.35,
      vignette: 0.38
    }
  },
  {
    id: "violet",
    label: "Violet Drift",
    motif: "arches",
    style: {
      variance: 0.36,
      vibrance: 0.78,
      brightness: 0.96,
      grain: 0.14,
      bokeh: 0.42,
      vignette: 0.32
    }
  },
  {
    id: "cobalt",
    label: "Cobalt Bloom",
    motif: "bokeh",
    style: {
      variance: 0.5,
      vibrance: 0.92,
      brightness: 0.98,
      grain: 0.11,
      bokeh: 0.68,
      vignette: 0.26
    }
  },
  {
    id: "bone",
    label: "Bone Minimal",
    motif: "mesh",
    style: {
      variance: 0.16,
      vibrance: 0.58,
      brightness: 1.08,
      grain: 0.19,
      bokeh: 0.12,
      vignette: 0.14
    }
  }
];

export const TEMPLATES: Array<{
  id: TemplateId;
  label: string;
  output: OutputSize;
  description: string;
  layout: Pick<StudioState["text"], "align" | "positionX" | "positionY" | "width">;
}> = [
  {
    id: "square-announcement",
    label: "Square announcement",
    output: "square",
    description: "Bold launch copy with generous safe area.",
    layout: { align: "left", positionX: 0.13, positionY: 0.2, width: 0.72 }
  },
  {
    id: "square-product",
    label: "Square product focus",
    output: "square",
    description: "Lower-set title with atmospheric space above.",
    layout: { align: "left", positionX: 0.12, positionY: 0.42, width: 0.76 }
  },
  {
    id: "wide-launch",
    label: "Wide launch",
    output: "wide",
    description: "Editorial copy left, signal system right.",
    layout: { align: "left", positionX: 0.1, positionY: 0.22, width: 0.46 }
  },
  {
    id: "wide-hero",
    label: "Wide hero",
    output: "wide",
    description: "Centered campaign lockup with deep atmosphere.",
    layout: { align: "center", positionX: 0.5, positionY: 0.22, width: 0.58 }
  }
];

export const DEFAULT_STATE: StudioState = {
  version: 3,
  seed: 2904817451,
  output: "wide",
  template: "wide-launch",
  text: {
    eyebrow: "WAVELET AUDIO · NEW INSTRUMENT",
    headline: "SOUND, SHAPED\nINTO MOTION",
    subline: "Boutique virtual instruments for modern composers and sound designers.",
    cta: "EXPLORE THE INSTRUMENT",
    align: "left",
    positionX: 0.1,
    positionY: 0.22,
    width: 0.46,
    tone: "light"
  },
  typography: {
    displayFont: "staatliches",
    bodyFont: "lato"
  },
  style: {
    palette: "violet",
    motif: "arches",
    variance: 0.36,
    vibrance: 0.78,
    brightness: 0.96,
    grain: 0.14,
    bokeh: 0.42,
    vignette: 0.32
  },
  animation: {
    duration: 10,
    fps: 60,
    colorDrift: 0.45,
    bokehDrift: 0.38,
    shapeDrift: 0.26
  }
};

export const STORAGE_KEY = "wavelet-studio:v3:presets";
export const VERSION_TWO_STORAGE_KEY = "wavelet-studio:v2:presets";
export const LEGACY_STORAGE_KEY = "wavelet-studio:v1:presets";
export const MAX_SHARE_STATE_LENGTH = 8192;
